import React, { useRef, useState, useEffect } from 'react';
import PropTypes from 'prop-types';

import { DropdownButton, FormControl } from 'react-bootstrap';
import { Button, IconButton, Tooltip, Checkbox, FormControlLabel } from '@sumologic/yama-ds-components';

import Icon from '~/components/Icon/Icon';
import Translate from '~/components/Translate/Translate';

//----------------------------------------------------------------------------------------------------------------------

const IconWithTip = props => {
    const content = (
        <Icon size={props.size} type={props.type} className={props.className} />
    );
    return props.noTip ? (
        content
    ) : (
        <Tooltip
            title={props.children}
            placement={props.placement || 'bottom'}
            arrow
        >
            {content}
        </Tooltip>
    );
};

//----------------------------------------------------------------------------------------------------------------------

const QueryAction = ({ filterValue, queries, updateQueries }) => {
    const updateQuerySelected = update => {
        const newQueries = [];
        let updated;
        if (queries?.length) {
            for (const query of queries) {
                if (query) {
                    newQueries.push(
                        query.selected && update
                            ? (updated = { ...query, ...update })
                            : query
                    );
                }
            }
        }
        // console.log({ queries, update, updated, newQueries });
        updateQueries(newQueries);
    };

    const addQueryAndSelect = newQuery => {
        const newQueries = [];
        if (queries?.length) {
            for (const query of queries) {
                if (query) {
                    query.selected = false;
                    newQueries.push(query);
                }
            }
        }
        if (newQuery) {
            newQuery.selected = true;
            newQueries.push(newQuery);
        }
        // console.log({ queries, newQuery, newQueries });
        updateQueries(newQueries);
    };

    let selectedQuery;
    let filterIsQuery;

    let initialName;
    let initialBookmark;

    const hasFilter = !!filterValue;
    const hasQueries = queries?.length > 0;

    if (hasQueries) {
        for (const query of queries) {
            if (query) {
                if (
                    query.selected // 𝘤𝘧𝘳 PreferencesManager selectedQueryMatcher
                ) {
                    selectedQuery = query;
                    initialName = query.name;
                    initialBookmark = query.bookmark;
                    if (!hasFilter || filterIsQuery) {
                        break;
                    }
                }
                if (hasFilter && filterValue === query.value) {
                    filterIsQuery = true;
                    if (selectedQuery) {
                        break;
                    }
                }
            }
        }
    }

    const filterIsSelectedQuery =
        filterIsQuery && !!selectedQuery && filterValue === selectedQuery.value;

    const checkName = value => {
        let isValid = false;
        const mangle = e => e?.trim().toLowerCase();
        const str = mangle(value);
        if (str) {
            isValid = true;
            if (hasQueries) {
                for (const query of queries) {
                    const queryName = mangle(query.name);
                    if (queryName) {
                        if (queryName === str) {
                            isValid = !!query.selected && filterIsSelectedQuery;
                            break;
                        }
                    }
                }
            }
        }
        return isValid; // 𝘢𝘤𝘵𝘶𝘢𝘭 𝘰𝘳 𝘶𝘯𝘶𝘴𝘦𝘥
    };

    /**-/
    console.group('QueryAction');
    console.log({ hasFilter, filterValue });
    console.log({ filterIsQuery, filterIsSelectedQuery });
    console.log({ hasQueries, queries });
    console.log({ selectedQuery, initialName, initialBookmark });
    console.groupEnd();
    /**/

    const {
        Object: { entries, assign }
    } = window;

    const [state, _setState] = useState({
        /*
            panelIsOpen
            saveable
            bookmark
            name
        */
    });

    const ref = useRef({
        state: state
    }).current;

    const updateState = updates => {
        if (ref.isMounted) {
            const prev = ref.state;
            for (const [key, value] of updates ? entries(updates) : []) {
                if (value !== prev[key]) {
                    const next = assign({}, prev, updates);
                    // console.log('𝘶𝘱𝘥𝘢𝘵𝘦𝘚𝘵𝘢𝘵𝘦', { prev, next }, updates);
                    _setState((ref.state = next));
                    return;
                }
            }
            // console.debug('𝘥𝘪𝘴𝘤𝘢𝘳𝘥𝘦𝘥', update);
        }
    };

    useEffect(() => {
        ref.isMounted = true;
        return () => {
            ref.isMounted = false;
            clearTimeout(ref.nameTimeout);
        };
    }, []);

    return (
        hasFilter && (
            <>
                {selectedQuery && !filterIsQuery && (
                    <div className="input-group-append d-flex">
                        <IconButton
                            onClick={() => {
                                updateQuerySelected({
                                    value: filterValue
                                });
                            }}
                            size="small"
                        >
                            <IconWithTip type="floppy-disk" size="sm">
                                <Translate message="Update current query" />
                            </IconWithTip>
                        </IconButton>
                    </div>
                )}
                <div
                    ref={el => {
                        ref.dropdownWrapper = el;
                    }}
                    className="save-query d-flex dropdown input-group-append"
                >
                    <DropdownButton
                        onToggle={bool => {
                            updateState({
                                panelIsOpen: bool
                            });
                        }}
                        alignRight
                        variant="outline-light"
                        className="save-query text-primary"
                        title={
                            <IconWithTip
                                size="md"
                                noTip={state.panelIsOpen}
                                type={
                                    filterIsQuery ? 'star-fill' : 'star' // ★ ☆
                                }
                                className={
                                    filterIsQuery ? 'text-secondary' : null
                                }
                            >
                                <Translate
                                    message={
                                        filterIsQuery
                                            ? 'Update query name'
                                            : 'Save new query'
                                    }
                                />
                            </IconWithTip>
                        }
                    >
                        <div
                            key={state.panelIsOpen}
                            className="query-panel"
                            data-testid="query-panel"
                        >
                            <Translate message="Query name">
                                {text => (
                                    <FormControl
                                        data-testid="query-name-input"
                                        type="text"
                                        placeholder={text}
                                        autoFocus={true}
                                        defaultValue={initialName ?? ''}
                                        onChange={event => {
                                            const name = event.target.value;
                                            clearTimeout(ref.nameTimeout);
                                            ref.nameTimeout = setTimeout(() => {
                                                let saveable = checkName(name);
                                                if (saveable) {
                                                    const item = selectedQuery;
                                                    if (item) {
                                                        saveable =
                                                            (state.bookmark ??
                                                                initialBookmark) !==
                                                                item.bookmark ||
                                                            name !== item.name;
                                                    }
                                                }
                                                updateState({
                                                    name: name,
                                                    saveable: saveable
                                                });
                                            }, 250);
                                        }}
                                    />
                                )}
                            </Translate>
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        defaultChecked={initialBookmark ?? false}
                                        disabled={!state.name}
                                        onChange={event => {
                                            const bookmark = event.target.checked;
                                            const name = state.name;
                                            let saveable = checkName(name);
                                            if (saveable) {
                                                const item = selectedQuery;
                                                if (item) {
                                                    saveable =
                                                        bookmark !== item.bookmark ||
                                                        name !== item.name;
                                                }
                                            }
                                            updateState({
                                                bookmark: bookmark,
                                                saveable: saveable
                                            });
                                        }}
                                        inputProps={{ id: 'bookmark-checkbox', 'data-testid': 'bookmark-checkbox' }}
                                    />
                                }
                                label="Add to bookmarks"
                                className="my-3"
                            />
                            <Button
                                variant="contained"
                                color="primary"
                                className="d-block w-100 m-0"
                                data-testid={`btn-query-${
                                    filterIsQuery ? 'update' : 'save'
                                }`}
                                disabled={!state.saveable}
                                onClick={() => {
                                    const newData = {
                                        bookmark: state.bookmark,
                                        name: state.name
                                    };
                                    updateState({
                                        saveable: false // todo: define a strong deterministic check for pending save
                                    });
                                    if (filterIsSelectedQuery) {
                                        updateQuerySelected(newData);
                                    } else {
                                        addQueryAndSelect({
                                            ...newData,
                                            value: filterValue
                                        });
                                    }
                                    // 𝘵𝘳𝘪𝘨𝘨𝘦𝘳 𝘤𝘭𝘪𝘤𝘬 𝘵𝘰 𝘤𝘭𝘰𝘴𝘦 𝘲𝘶𝘦𝘳𝘺-𝘱𝘢𝘯𝘦𝘭
                                    ref.dropdownWrapper?.parentElement?.click();
                                }}
                            >
                                <Translate
                                    message={
                                        filterIsQuery
                                            ? 'Update query'
                                            : 'Save query'
                                    }
                                />
                            </Button>
                        </div>
                    </DropdownButton>
                </div>
            </>
        )
    );
};

QueryAction.propTypes = {
    filterValue: PropTypes.string,
    queries: PropTypes.array,
    addQuery: PropTypes.func,
    updateQueries: PropTypes.func
};

export default QueryAction;
